import { z } from 'zod';
import { UuidSchema } from './primitives/uuid.js';
import { ProfileAnalysisStructureSchema } from './profile-analysis.js';
import { ExternalLinkContentMetadataSchema } from './shared/link-content.js';

const AnalysisBlocksSchema = z.object({
  blocks: z.array(
    z.object({
      type: z.string(),
      content: z.unknown(),
    })
  ),
});

export const UserAnalysisSchema = ProfileAnalysisStructureSchema;

export const UserAnalysisMetadataSchema = z
  .object({
    model: z.string().optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
    analysisId: UuidSchema.optional(),
    fetchExternalUrls: z.boolean().optional(),
    maxLinkSummaries: z.number().int().min(0).max(50).optional(),
    linkContent: ExternalLinkContentMetadataSchema,
  })
  .passthrough();

export const UserAnalysisCacheResponseSchema = z.object({
  analysis: z.union([UserAnalysisSchema, AnalysisBlocksSchema, z.null()]).nullish(),
  cached: z.boolean().default(true),
  updatedAt: z.date().nullable(),
  timestamp: z.date().optional(),
  metadata: UserAnalysisMetadataSchema.nullable().optional(),
});

export const UserAnalysisRequestSchema = z.object({
  profileId: z.string().trim().min(1, 'profileId is required').optional(),
  forceRefresh: z.boolean().optional(),
  streamingEnabled: z.boolean().optional(),
  dateRange: z
    .object({
      from: z.date(),
      to: z.date(),
    })
    .optional(),
  maxTweets: z.number().int().positive().optional(),
  includeReplies: z.boolean().optional(),
  includeRetweets: z.boolean().optional(),
  model: z.string().optional(),
  fetchExternalUrls: z.boolean().optional(),
  maxLinkSummaries: z.number().int().min(0).max(50).optional(),
});

export const UserAnalysisStartDataSchema = z.object({
  analysisId: UuidSchema,
  jobId: z.string().optional(),
  cached: z.boolean(),
  analysis: z.union([UserAnalysisSchema, z.null()]).optional(),
  metadata: z.union([UserAnalysisMetadataSchema, z.record(z.string(), z.unknown()), z.null()]).optional(),
  token: z.union([z.record(z.string(), z.unknown()), z.null()]).optional(),
  streamUrl: z.string().nullable().optional(),
  status: z.string().optional(),
  updatedAt: z.date().optional(),
});

const UserAnalysisStartEnvelopeSchema = z
  .object({
    success: z.literal(true),
    data: UserAnalysisStartDataSchema,
    message: z.string().nullable().optional(),
  })
  .passthrough();

const UserAnalysisStartCliSchema = UserAnalysisStartDataSchema.extend({
  message: z.string().optional(),
}).passthrough();

export const UserAnalysisStartResponseSchema = z.union([UserAnalysisStartEnvelopeSchema, UserAnalysisStartCliSchema]);

export const UserAnalysisCancelResponseSchema = z.object({
  success: z.boolean(),
});

export { RealtimeTokenSchema as UserAnalysisRealtimeTokenResponseSchema } from './shared/realtime.js';

export const UserAnalysisRealtimeTokenRequestSchema = z.object({
  analysisId: UuidSchema,
});

export const UserAnalysisDeleteRequestSchema = z.object({
  targetUserId: z.string().trim().pipe(UuidSchema),
});

export const UserAnalysisDeleteResponseSchema = z.object({
  success: z.literal(true),
  deleted: z.number().int().min(0),
});

export const UserAnalysisHistoryItemSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    timeRange: z.string().optional(),
    createdAt: z.date(),
    expiresAt: z.date().nullable().optional(),
    lastAccessedAt: z.date().optional(),
    metadata: UserAnalysisMetadataSchema.nullable().optional(),
    analysis: z.union([UserAnalysisSchema, AnalysisBlocksSchema, z.null()]).optional(),
  })
  .strip();

export const UserAnalysisHistoryResponseSchema = z
  .object({
    reports: z.array(UserAnalysisHistoryItemSchema),
    newTweetCounts: z.record(z.string(), z.number()).optional(),
  })
  .strip();

export const UserAnalysisHistoryQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  })
  .strip();

export const UserAnalysisRealtimeTokenSchema = z
  .object({
    channel: z.string(),
    topics: z.array(z.string()),
    key: z.string().nullable().optional(),
  })
  .passthrough()
  .nullable()
  .optional();

export const UserAnalysisPathParamsSchema = z.object({ profileId: z.string().trim().min(1) });
export type UserAnalysisPathParams = z.infer<typeof UserAnalysisPathParamsSchema>;

export const UserAnalysisForceRefreshQuerySchema = z
  .object({
    forceRefresh: z.coerce.boolean().optional(),
  })
  .partial();
export type UserAnalysisForceRefreshQuery = z.infer<typeof UserAnalysisForceRefreshQuerySchema>;

export const UserAnalysisCancelPathParamsSchema = z.object({ analysisId: UuidSchema });
export type UserAnalysisCancelPathParams = z.infer<typeof UserAnalysisCancelPathParamsSchema>;

export const UserAnalysisCancelRequestSchema = z.undefined();
export type UserAnalysisCancelRequest = z.infer<typeof UserAnalysisCancelRequestSchema>;
